import { useEffect, useState } from 'react';
import { apiFetch } from '../../api';
import { withFeatureLabels } from '../../featureLabels';


// Hooks for normative model data
export const useNormativeFeatures = () => {
    const [features, setFeatures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchFeatures = async () => {
            try {
                setLoading(true);
                const response = await apiFetch('/api/normative-features');

                // Check if response is not JSON
                const contentType = response.headers.get("content-type");
                if (!contentType || !contentType.includes("application/json")) {
                    throw new Error("No normative data available");
                }

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || `HTTP error! Status: ${response.status}`);
                }

                const data = await response.json();
                setFeatures(withFeatureLabels(data));
                setError(null);
            } catch (err) {
                console.error('Error fetching normative features:', err);
                setError(err.message || "Failed to load normative features");
                setFeatures([]);
            } finally {
                setLoading(false);
            }
        };

        fetchFeatures();
    }, []);

    return { features, loading, error };
};


export const useNormativeRegions = () => {
    const [regions, setRegions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchRegions = async () => {
            try {
                setLoading(true);
                // Fetch the CSV file from the public folder
                const response = await fetch('/atlas/brain_region_map.csv');

                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }

                const csvText = await response.text();

                // Parse CSV - simple parser that handles quoted values
                const parseCSVLine = (line) => {
                    const result = [];
                    let current = '';
                    let inQuotes = false;

                    for (let i = 0; i < line.length; i++) {
                        const char = line[i];
                        if (char === '"') {
                            inQuotes = !inQuotes;
                        } else if (char === ',' && !inQuotes) {
                            result.push(current.trim());
                            current = '';
                        } else {
                            current += char;
                        }
                    }
                    result.push(current.trim());
                    return result;
                };

                const lines = csvText.split('\n').filter(line => line.trim() !== '');
                if (lines.length < 2) {
                    throw new Error("CSV file is empty or invalid");
                }

                // Parse header
                const headers = parseCSVLine(lines[0]);
                const regionNameIndex = headers.indexOf('region_name');

                if (regionNameIndex === -1) {
                    throw new Error("region_name column not found in CSV");
                }

                // Extract unique region names
                const regionNamesSet = new Set();
                for (let i = 1; i < lines.length; i++) {
                    const values = parseCSVLine(lines[i]);
                    if (values[regionNameIndex]) {
                        regionNamesSet.add(values[regionNameIndex]);
                    }
                }

                // Convert to array and sort
                const uniqueRegionNames = Array.from(regionNamesSet).sort();

                // Format as region objects
                const regionObjects = uniqueRegionNames.map(regionName => ({
                    id: regionName,
                    label: regionName
                }));

                setRegions(regionObjects);
                setError(null);
            } catch (err) {
                console.error('Error fetching normative regions:', err);
                setError(err.message || "Failed to load normative regions");
                setRegions([]);
            } finally {
                setLoading(false);
            }
        };

        fetchRegions();
    }, []);

    return { regions, loading, error };
};


export const useNormativeModels = (selectedFeatures, selectedRegions, montageType = 'Bipolar') => {
    const [models, setModels] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (selectedFeatures.length === 0 && selectedRegions.length === 0) {
            setModels([]);
            setLoading(false);
            setError(null);
            return undefined;
        }

        let ignore = false;

        const fetchModels = async () => {
            try {
                setLoading(true);

                const featuresToFetch = selectedFeatures.length > 0
                    ? selectedFeatures
                    : [null];

                const modelResponses = await Promise.all(featuresToFetch.map(async (featureName) => {
                    const featureQuery = featureName
                        ? `feature=${encodeURIComponent(featureName)}&`
                        : '';
                    const response = await apiFetch(
                        `/api/normative-models?${featureQuery}montage=${encodeURIComponent(montageType)}`
                    );

                    const contentType = response.headers.get("content-type");
                    if (!contentType || !contentType.includes("application/json")) {
                        throw new Error("No normative data available");
                    }

                    if (!response.ok) {
                        if (response.status === 404) {
                            return [];
                        }
                        const errorData = await response.json();
                        throw new Error(errorData.message || errorData.error || `HTTP error! Status: ${response.status}`);
                    }

                    const data = await response.json();
                    return Array.isArray(data) ? data : [];
                }));

                const allModels = modelResponses.flat();

                // Filter models based on selected features and regions
                let filteredModels = allModels;

                if (selectedRegions.length > 0) {
                    filteredModels = filteredModels.filter(model =>
                        selectedRegions.includes(model.region_name)
                    );
                }

                if (!ignore) {
                    setModels(filteredModels);
                    setError(null);
                }
            } catch (err) {
                console.error('Error fetching normative models:', err);
                if (!ignore) {
                    setError(err.message || "Failed to load normative models");
                    setModels([]);
                }
            } finally {
                if (!ignore) {
                    setLoading(false);
                }
            }
        };

        fetchModels();

        return () => {
            ignore = true;
        };
    }, [selectedFeatures, selectedRegions, montageType]);

    return { models, loading, error };
};


// New hook to fetch channel values for a specific region and feature
export const useChannelValues = (regionName, featureName, montageType = 'Bipolar') => {
    const [channelValues, setChannelValues] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        // Only fetch if both region and feature are specified
        if (!regionName || !featureName) {
            setChannelValues([]);
            return;
        }

        const fetchChannelValues = async () => {
            try {
                setLoading(true);
                const response = await apiFetch(`/api/channel-values?region=${encodeURIComponent(regionName)}&feature=${encodeURIComponent(featureName)}&montage=${encodeURIComponent(montageType)}`);

                // Check if response is not JSON
                const contentType = response.headers.get("content-type");
                if (!contentType || !contentType.includes("application/json")) {
                    throw new Error("No channel data available");
                }

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || `HTTP error! Status: ${response.status}`);
                }

                const data = await response.json();
                setChannelValues(data);
                setError(null);
            } catch (err) {
                console.error(`Error fetching channel values for ${regionName}/${featureName}:`, err);
                setError(err.message || "Failed to load channel values");
                setChannelValues([]);
            } finally {
                setLoading(false);
            }
        };

        fetchChannelValues();
    }, [regionName, featureName, montageType]);

    return { channelValues, loading, error };
};


// Fetch z-scores for a specific region and feature
export const useZScores = (regionName, featureName, montageType = 'Bipolar') => {
    const [zScores, setZScores] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        // Only fetch if both region and feature are specified
        if (!regionName || !featureName) {
            setZScores([]);
            return;
        }

        const fetchZScores = async () => {
            try {
                setLoading(true);
                const response = await apiFetch(`/api/z-scores?region=${encodeURIComponent(regionName)}&feature=${encodeURIComponent(featureName)}&montage=${encodeURIComponent(montageType)}`);

                // Check if response is not JSON
                const contentType = response.headers.get("content-type");
                if (!contentType || !contentType.includes("application/json")) {
                    throw new Error("No z-score data available");
                }

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || `HTTP error! Status: ${response.status}`);
                }

                const data = await response.json();
                setZScores(data);
                setError(null);
            } catch (err) {
                console.error(`Error fetching z-scores for ${regionName}/${featureName}:`, err);
                setError(err.message || "Failed to load z-scores");
                setZScores([]);
            } finally {
                setLoading(false);
            }
        };

        fetchZScores();
    }, [regionName, featureName, montageType]);

    return { zScores, loading, error };
};
